export type AnalyticsProps = Record<string, string | number | boolean>;

/** Transport hints for a single event; `immediate` bypasses destination batching. */
export interface TrackOptions {
  immediate?: boolean;
}

export interface AnalyticsAdapter {
  track(event: string, props?: AnalyticsProps, options?: TrackOptions): void;
  identify(anonId: string): void;
  setUserProps(props: AnalyticsProps): void;
  flush(): void;
  optOut(): void;
  reset(): void;
}

interface BufferedEvent {
  event: string;
  props?: AnalyticsProps;
  ts: number;
}

const STORAGE_KEY = "bgb.analytics.buffer";

function safeRead(): BufferedEvent[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as BufferedEvent[]) : [];
  } catch {
    return [];
  }
}

function safeWrite(buffer: BufferedEvent[]): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    return;
  }
}

/**
 * MVP analytics adapter. Buffers events in localStorage, mirrors them to the
 * console for debugging, and never throws when storage is unavailable.
 */
export class LocalAnalyticsAdapter implements AnalyticsAdapter {
  private anonId: string | null = null;
  private userProps: AnalyticsProps = {};

  track(event: string, props?: AnalyticsProps): void {
    const entry: BufferedEvent = { event, props, ts: Date.now() };
    const buffer = safeRead();
    safeWrite([...buffer, entry]);
    if (typeof console !== "undefined") {
      console.debug("[analytics]", event, props ?? {});
    }
  }

  identify(anonId: string): void {
    this.anonId = anonId;
  }

  setUserProps(props: AnalyticsProps): void {
    this.userProps = { ...this.userProps, ...props };
  }

  flush(): void {
    return;
  }

  optOut(): void {
    return;
  }

  reset(): void {
    this.anonId = null;
  }

  exportJson(): string {
    return JSON.stringify(
      { anonId: this.anonId, userProps: this.userProps, events: safeRead() },
      null,
      2,
    );
  }
}

/**
 * Fans out every call to a list of downstream adapters (PostHog, Sentry, etc.).
 * A failure in one destination never breaks the others.
 */
export class CompositeAnalyticsAdapter implements AnalyticsAdapter {
  private readonly destinations: ReadonlyArray<AnalyticsAdapter>;

  constructor(destinations: ReadonlyArray<AnalyticsAdapter>) {
    this.destinations = destinations;
  }

  private forEach(action: (adapter: AnalyticsAdapter) => void): void {
    for (const destination of this.destinations) {
      try {
        action(destination);
      } catch (error) {
        if (typeof console !== "undefined") {
          console.warn("[analytics] destination failed", error);
        }
      }
    }
  }

  track(event: string, props?: AnalyticsProps, options?: TrackOptions): void {
    this.forEach((adapter) =>
      options ? adapter.track(event, props, options) : adapter.track(event, props),
    );
  }

  identify(anonId: string): void {
    this.forEach((adapter) => adapter.identify(anonId));
  }

  setUserProps(props: AnalyticsProps): void {
    this.forEach((adapter) => adapter.setUserProps(props));
  }

  flush(): void {
    this.forEach((adapter) => adapter.flush());
  }

  optOut(): void {
    this.forEach((adapter) => adapter.optOut());
  }

  reset(): void {
    this.forEach((adapter) => adapter.reset());
  }
}

let adapter: AnalyticsAdapter = new LocalAnalyticsAdapter();

export function getAnalyticsAdapter(): AnalyticsAdapter {
  return adapter;
}

export function setAnalyticsAdapter(nextAdapter: AnalyticsAdapter): void {
  adapter = nextAdapter;
}
